// useMarketingPrograms — shared hook for the public /api/marketing/programs
// list. Module-level cache + single in-flight promise so Landing and Footer
// (and any future marketing surface) share one network call per page load.
import { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";

let cached = null;        // resolved list (or null until first fetch)
let inFlight = null;      // promise of the current fetch
const subscribers = new Set();

function fetchOnce() {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = apiGet("/marketing/programs", { auth: false })
    .then((data) => {
      cached = Array.isArray(data) ? data : [];
      subscribers.forEach((cb) => cb(cached));
      return cached;
    })
    .catch(() => {
      cached = [];
      subscribers.forEach((cb) => cb(cached));
      return cached;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

export default function useMarketingPrograms() {
  const [programs, setPrograms] = useState(cached);
  useEffect(() => {
    let active = true;
    const cb = (next) => { if (active) setPrograms(next); };
    subscribers.add(cb);
    fetchOnce().then((next) => { if (active) setPrograms(next); });
    return () => { active = false; subscribers.delete(cb); };
  }, []);
  return programs; // null while loading on first ever call, then array (possibly empty)
}

// Test/dev hook — clear the cache (not used in app code).
export function _resetMarketingProgramsCacheForTests() {
  cached = null;
  inFlight = null;
}
